import {useCallback, useRef, useState} from 'react';

const DEFAULT_SWIPE_THRESHOLD = 100;
const DEFAULT_ROTATION_FACTOR = 0.1;

type UseCardSwipeOptions = {
  swipeThreshold?: number;
  rotationFactor?: number;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  disabled?: boolean;
};

type UseCardSwipeReturn = {
  isDragging: boolean;
  translateX: number;
  rotation: number;
  handleDragStart: (e: React.MouseEvent | React.TouchEvent) => void;
  handleDragMove: (e: React.MouseEvent | React.TouchEvent) => void;
  handleDragEnd: () => void;
  resetPosition: () => void;
};

export function useCardSwipe({
  swipeThreshold = DEFAULT_SWIPE_THRESHOLD,
  rotationFactor = DEFAULT_ROTATION_FACTOR,
  onSwipeLeft,
  onSwipeRight,
  disabled = false,
}: UseCardSwipeOptions = {}): UseCardSwipeReturn {
  const [isDragging, setIsDragging] = useState(false);
  const [translateX, setTranslateX] = useState(0);

  const startXRef = useRef(0);
  const currentXRef = useRef(0);

  const rotation = translateX * rotationFactor;

  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled) return;

      setIsDragging(true);
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      startXRef.current = clientX;
      currentXRef.current = clientX;
    },
    [disabled],
  );

  const handleDragMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDragging) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      currentXRef.current = clientX;
      const deltaX = clientX - startXRef.current;
      setTranslateX(deltaX);
    },
    [isDragging],
  );

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return;

    setIsDragging(false);
    const deltaX = currentXRef.current - startXRef.current;

    if (Math.abs(deltaX) >= swipeThreshold) {
      const direction = deltaX > 0 ? 'right' : 'left';

      if (direction === 'left' && onSwipeLeft) {
        onSwipeLeft();
      } else if (direction === 'right' && onSwipeRight) {
        onSwipeRight();
      } else {
        setTranslateX(0);
      }
    } else {
      setTranslateX(0);
    }
  }, [isDragging, swipeThreshold, onSwipeLeft, onSwipeRight]);

  const resetPosition = useCallback(() => {
    setTranslateX(0);
    setIsDragging(false);
  }, []);

  return {
    isDragging,
    translateX,
    rotation,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    resetPosition,
  };
}
